import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Prompt, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { useSelector } from 'store';
import { Loaders, Title } from 'shared';
import { useTranslation } from 'hooks/useTranslation';
import { clearOrdersState, createOrderRequest } from 'features/dashboard/redux/actions';
import { SendGiftStepper } from 'features/dashboard/components/SendGiftStepper';
import { PersonalInformationStep } from 'features/dashboard/components/PersonalInformationStep';
import { GiftInformationStep } from 'features/dashboard/components/GiftInformationStep';
import { ReviewDetailsStep } from 'features/dashboard/components/ReviewDetailsStep';
import { CreateOrderInput } from 'features/dashboard/ts/CreateOrderInput';
import { Paper } from 'features/app/components/Paper';
import { SuccessfulAction } from 'features/dashboard/components/SuccessfulAction';

import { useStyles } from './styles';
import { parseFieldErrors } from 'utils/error-utils';
import { ReactComponent as PlaneIcon } from 'assets/icons/plane-icon.svg';

const INITIAL_FORM_STATE: CreateOrderInput = {
  country: null,
  product: null,
  currency: null,
  last_name: '',
  first_name: '',
  email: '',
  phone_number: '',
  amount: 1,
  quantity: 1,
  company: '',
  denomination: 0,
  designation: '',
};

export const SendSingleGiftPage = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const ordersState = useSelector(state => state.dashboard.orders);
  const [currentStep, setCurrentStep] = useState(1);
  const [passedSteps, setPassedSteps] = useState<number[]>([]);

  const methods = useForm<CreateOrderInput>({
    mode: 'onSubmit',
    defaultValues: INITIAL_FORM_STATE,
  });

  const isFormValid = useCallback(async () => {
    return await methods.trigger(Object.keys(INITIAL_FORM_STATE) as (keyof CreateOrderInput)[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNextStep = useCallback(async () => {
    const isValid = await isFormValid();
    if (!isValid) return;

    setCurrentStep(currentStep + 1);
    setPassedSteps([...new Set([...passedSteps, currentStep])]);
  }, [isFormValid, currentStep, passedSteps, setCurrentStep, setPassedSteps]);

  const onStepChange = useCallback(
    async (step: number, includePassedSteps = false, validate = true) => {
      if (validate) {
        const isValid = await isFormValid();
        if (!isValid) return;
      }

      if (includePassedSteps) {
        setCurrentStep(step);
      } else {
        if (!passedSteps.includes(step)) return;
        setCurrentStep(step);
        setPassedSteps(Array.from({ length: step - 1 }, (_, i) => i + 1));
      }
    },
    [isFormValid, passedSteps, setCurrentStep, setPassedSteps]
  );

  const handleStepChange = (step: number, completed: boolean) => {
    if (!completed) return;

    step !== currentStep && onStepChange(step, true);
  };

  const onSubmit = () => {
    const {
      currency,
      product,
      amount,
      quantity,
      phone_number,
      email,
      first_name,
      last_name,
      country,
      designation,
      company,
      denomination,
    } = methods.watch();

    if (!product || !currency) return;
    const inputData = {
      branded_giftcard: product.id,
      currency: currency.currency_id,
      first_name,
      last_name,
      email,
      company,
      designation,
      phone_number,
      denomination: denomination || null,
      gift_card_amount: amount,
      quantity,
      country: country?.code,
    };

    dispatch(createOrderRequest(inputData));
  };

  const handleOrderCreated = useCallback(() => {
    dispatch(clearOrdersState());
    methods.reset();
    onStepChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, onStepChange]);

  useEffect(() => {
    if (currentStep !== 3) return;

    (async () => {
      const isValid = await isFormValid();
      !isValid && (await onStepChange(1));
    })();
  }, [currentStep, isFormValid, onStepChange]);

  useEffect(() => {
    const unlisten = history.listen(() => {
      handleOrderCreated();
    });

    return () => unlisten();
  }, [handleOrderCreated, history]);

  useEffect(() => {
    if (!ordersState.error) return;
    parseFieldErrors<CreateOrderInput>(ordersState?.error as unknown as Record<keyof CreateOrderInput, []>).forEach(
      ({ field, ...error }) => {
        methods.setError(field, error);
      }
    );
    dispatch(clearOrdersState());
    onStepChange(1, false, false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, ordersState.error, onStepChange]);

  return (
    <>
      <Prompt
        when={methods.formState.isDirty && !ordersState?.isOrderCreated}
        message={t('formLeavingPrompt', { ns: 'common' })}
      />
      <Title title={t('sendSingleReward', { ns: 'dashboard' })} />
      <FormProvider {...methods}>
        {ordersState.isLoading ? (
          <Loaders.Loader />
        ) : ordersState.isOrderCreated ? (
          <SuccessfulAction onSuccessfulAction={handleOrderCreated} icon={PlaneIcon} />
        ) : (
          <Paper borderRadius={20} className={classes.sendGiftContainer}>
            <SendGiftStepper passedSteps={passedSteps} onStepClick={handleStepChange} />

            {currentStep === 1 ? (
              <PersonalInformationStep onSubmit={onNextStep} />
            ) : currentStep === 2 ? (
              <GiftInformationStep onSubmit={onNextStep} />
            ) : currentStep === 3 ? (
              <ReviewDetailsStep onSubmit={onSubmit} />
            ) : (
              <PersonalInformationStep onSubmit={onNextStep} />
            )}
          </Paper>
        )}
      </FormProvider>
    </>
  );
};
